import { useToast } from '@/hooks/use-toast';
import html2canvas from 'html2canvas';

export function useChartBulkExport() {
  const { toast } = useToast();

  const exportChartToPNG = async (elementId: string, filename: string, hideTitle: boolean = false) => {
    try {
      const element = document.getElementById(elementId);
      if (!element) {
        throw new Error(`Element ${elementId} niet gevonden`);
      }

      // Clone element to modify
      const clone = element.cloneNode(true) as HTMLElement;
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      document.body.appendChild(clone);

      // Hide title if requested
      if (hideTitle) {
        const cardHeaders = clone.querySelectorAll('[class*="CardHeader"], [class*="card-header"]');
        cardHeaders.forEach(header => {
          (header as HTMLElement).style.display = 'none';
        });
      }

      // Capture as canvas
      const canvas = await html2canvas(clone, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });

      // Remove clone
      document.body.removeChild(clone);

      // Convert to blob
      return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob'));
          }
        });
      });
    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  };

  const exportAllCharts = async (chartIds: Array<{ id: string; filename: string }>, hideTitle: boolean = false) => {
    try {
      toast({
        title: 'Export gestart',
        description: `${chartIds.length} grafieken worden geëxporteerd...`,
      });

      let successCount = 0;
      for (const { id, filename } of chartIds) {
        try {
          const blob = await exportChartToPNG(id, filename, hideTitle);
          
          // Download
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `${filename}.png`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
          
          successCount++;
          
          // Small delay between downloads to prevent browser blocking
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (error) {
          console.error(`Failed to export ${filename}:`, error);
        }
      }

      toast({
        title: 'Export voltooid',
        description: `${successCount} van ${chartIds.length} grafieken succesvol geëxporteerd`,
      });
    } catch (error) {
      console.error('Bulk export error:', error);
      toast({
        title: 'Export mislukt',
        description: 'Er is een fout opgetreden bij het exporteren',
        variant: 'destructive',
      });
    }
  };

  return { exportAllCharts, exportChartToPNG };
}
